import { displaySku, schemaAvailability } from "./display-sku";
import type { Category, Product, Store } from "./types";

export function productPath(slug: string): string {
  return `/pasar/produk/${slug}`;
}

export function productJsonLd(product: Product, origin: string) {
  const sku = displaySku(product);
  const url = `${origin}${productPath(product.slug)}`;
  const availability = schemaAvailability(sku?.availability, sku?.available);
  const offer = {
    "@type": "Offer",
    url,
    price: String(sku?.price ?? 0),
    priceCurrency: "IDR",
    availability,
    sku: sku?.sku_code,
  };

  const crumbs = [
    { "@type": "ListItem", position: 1, name: "Pasar Kupang", item: `${origin}/pasar` },
    product.category
      ? {
          "@type": "ListItem",
          position: 2,
          name: product.category.name,
          item: `${origin}/pasar/kategori/${product.category.slug}`,
        }
      : null,
    {
      "@type": "ListItem",
      position: product.category ? 3 : 2,
      name: product.name,
      item: url,
    },
  ].filter(Boolean);

  return [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description,
      url,
      brand: { "@type": "Brand", name: product.merchant.name },
      offers: offer,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs,
    },
  ];
}

export function collectionJsonLd(
  name: string,
  url: string,
  products: Product[],
  origin: string,
) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name,
      url,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: products.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${origin}${productPath(product.slug)}`,
          name: product.name,
        })),
      },
    },
  ];
}

export function categoryJsonLd(category: Category, origin: string) {
  return collectionJsonLd(
    category.name,
    `${origin}/pasar/kategori/${category.slug}`,
    category.products,
    origin,
  );
}

export function storeJsonLd(store: Store, origin: string) {
  const url = `${origin}/pasar/toko/${store.slug}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "OnlineStore",
      name: store.name,
      description: store.description,
      url,
    },
  ];
}

export function homeJsonLd(products: Product[], origin: string) {
  return collectionJsonLd("Pasar Kupang", `${origin}/pasar`, products, origin);
}
